<script setup>
import { inject, ref, computed, watch, nextTick } from 'vue'
import useMpm from '../Mpm/useMpm'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, A11y } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/autoplay'

const props = defineProps({
  size: { type: String, default: null },
  mpms: {
    type: Array,
    default: () => []
  }
})

const categoryInfo = inject('categoryInfo')
const { mpmImpression, mpmTracking, getVisible } = useMpm()
const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const isBigScreen = computed(() => ['xl', 'l'].includes(props.size))
const twoMpms = computed(() => (props.mpms.length === 2 ? [...props.mpms, ...props.mpms] : []))
const _mpms = computed(() => (twoMpms.value.length === 0 ? props.mpms : twoMpms.value))

const tvbPlayerRef = ref()
const mpmRef = ref()
const realIndex = ref(-1)
const onActiveIndexChange = (e) => {
  if (!e?.autoplay?.running) mpmRef.value?.$el?.swiper?.autoplay?.start()
  realIndex.value = e.realIndex
}

// 监听播放
const playing = ref(false)
const onStateChange = ({ index, state }) => {
  if (index === realIndex.value) {
    if (state === 'play' || state === 'loading') {
      mpmRef.value?.$el?.swiper?.autoplay?.stop()
    } else {
      mpmRef.value?.$el?.swiper?.autoplay?.start()
    }
    playing.value = state !== 'ended'
  }
}

// 处理自动播放
const onSlideChangeTransitionEnd = () => {
  nextTick(async () => {
    if (Array.isArray(tvbPlayerRef.value)) {
      for (const element of tvbPlayerRef.value) {
        let visible = await getVisible(element)
        if (visible) {
          setTimeout(() => element?.play?.(), 300)
          break
        }
      }
    }
  })
}

// 初始化后
const onAfterInit = () => onSlideChangeTransitionEnd()

watch(realIndex, (value) => {
  playing.value = false
  if (Array.isArray(tvbPlayerRef.value)) {
    tvbPlayerRef.value.forEach((element) => element?.pause?.())
  }
  const res = props.mpms?.[value]
  mpmImpression({ ...res, category_name: categoryInfo.value?.title })
})

const onMouseover = (index) => {
  if (!['xl', 'l', 'm'].includes(props.size)) return
  mpmRef.value.$el.swiper.slideToLoop(index)
}
</script>

<template>
  <div
    class="tvb-main-category-mpm2"
    :class="[
      size ? 'mpm-' + size : '',
      { 'slide_image-hover': isBigScreen },
      { 'mpm-onlyone': mpms.length === 1 },
      { 'show-mpm_nav': isBigScreen && playing }
    ]"
  >
    <swiper
      ref="mpmRef"
      class="tvb-main-category-swiper"
      :class="{ 'tvb-hairline--surround': isBigScreen }"
      :modules="[Autoplay, A11y]"
      :loop="_mpms.length > 1"
      :autoplay="{
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      }"
      :spaceBetween="8"
      :slidesPerView="'auto'"
      :breakpoints="{
        768: {
          spaceBetween: 16
        },
        1025: {
          spaceBetween: 0
        }
      }"
      @afterInit="onAfterInit"
      @activeIndexChange="onActiveIndexChange"
      @slideChangeTransitionEnd="onSlideChangeTransitionEnd"
    >
      <swiper-slide
        v-for="(item, index) in _mpms"
        :key="index"
        class="tvb-main-category-swiper_slide"
        #="{ isActive }"
      >
        <template v-if="item.jump_param">
          <tvb-router-link
            :to="item.jump_param"
            :linkRel="item.link_rel"
            :linkTarget="item.link_target"
            @click="mpmTracking(item, item.jump_type === 1 ? 'image' : 'video')"
          >
            <tvb-image
              v-if="isBigScreen"
              class="tvb-main-category-swiper_slide_image"
              :src="item.pc_image_info"
              :alt="item.image_alt_text"
              :transform="isMobile ? false : true"
              backgroundc="#F5F6F7"
            />
            <tvb-image
              v-else
              class="tvb-main-category-swiper_slide_image"
              :src="item.h5_image_info"
              :alt="item.image_alt_text"
              :transform="isMobile ? false : true"
              backgroundc="#F5F6F7"
            />
          </tvb-router-link>
        </template>
        <template v-else>
          <tvb-image
            v-if="isBigScreen"
            class="tvb-main-category-swiper_slide_image"
            :src="item.pc_image_info"
            :alt="item.image_alt_text"
            :transform="isMobile ? false : true"
            backgroundc="#F5F6F7"
          />
          <tvb-image
            v-else
            class="tvb-main-category-swiper_slide_image"
            :src="item.h5_image_info"
            :alt="item.image_alt_text"
            :transform="isMobile ? false : true"
            backgroundc="#F5F6F7"
          />
        </template>
        <tvb-player
          v-if="item.jump_type === 3 && item.jump_param && isActive"
          ref="tvbPlayerRef"
          class="tvb-main-category_swiper-player"
          :class="{ 'slide-player-playing': isActive && playing }"
          :src="item.jump_param"
          :title="item?.title"
          :mute="true"
          :playerVars="{ fs: 0 }"
          @state-change="(player) => onStateChange({ ...player, index })"
        ></tvb-player>

        <div
          v-if="item.title"
          v-show="isBigScreen ? !playing : true"
          class="tvb-main-category-swiper_slide_mask"
          :class="{ 'mask-playing': isActive && playing }"
        >
          <tvb-router-link
            :to="item.jump_type === 1 ? item.jump_param : null"
            :linkRel="item.link_rel"
            :linkTarget="item.link_target"
            @click="mpmTracking(item, 'text', item.jump_type === 1 && item.jump_param)"
          >
            <h3 class="clear-h tvb-swiper_slide_mask-tit" :title="item.title">
              {{ item.title }}
            </h3>
          </tvb-router-link>
        </div>
      </swiper-slide>

      <template v-slot:container-end>
        <template v-if="mpms.length > 1">
          <div
            class="tvb-main-category-mpm_nav mpm_nav-prev"
            @click.stop="() => mpmRef.$el.swiper.slidePrev()"
          >
            <tvb-svg-icon name="icon_arrow_mpm" />
          </div>
          <div
            class="tvb-main-category-mpm_nav mpm_nav-next"
            @click.stop="() => mpmRef.$el.swiper.slideNext()"
          >
            <tvb-svg-icon name="icon_arrow_mpm" />
          </div>
        </template>
      </template>
    </swiper>

    <div v-show="isBigScreen ? !playing : true" class="tvb-main-category-mpm_page">
      <div class="tvb-main-category-mpm_page_wrap">
        <div
          class="tvb-main-category-mpm_dot"
          v-for="(item, index) in mpms"
          :key="index"
          :class="[
            {
              active:
                twoMpms.length === 0
                  ? realIndex === index
                  : realIndex === index || realIndex - 2 === index
            },
            { hover: isBigScreen }
          ]"
          @click="onMouseover(index)"
        ></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-main-category-mpm2 {
  box-sizing: border-box;
  position: relative;
  width: 100%;
  height: 100%;

  .tvb-main-category-swiper {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    --tvb-hairline-border-radius: calc(2 * 16px);
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  }

  // 播放一直显示
  &.show-mpm_nav {
    .tvb-main-category-mpm_nav {
      &.mpm_nav-prev {
        left: 0;
      }
      &.mpm_nav-next {
        right: 0;
      }
    }
  }

  // hover
  &.slide_image-hover {
    &:hover .tvb-main-category-mpm_nav {
      &.mpm_nav-prev {
        left: 0;
      }
      &.mpm_nav-next {
        right: 0;
      }
    }
  }

  // 导航按钮
  .tvb-main-category-mpm_nav {
    position: absolute;
    top: calc(200 / 477 * 100%);
    width: 28px;
    height: 48px;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    border-radius: 8px 0px 0px 8px;
    user-select: none;
    cursor: pointer;
    transition:
      left 0.15s linear,
      right 0.15s linear,
      background-color 0.15s linear;
    &.mpm_nav-prev {
      left: -28px;
      transform: scaleX(-1);
      transition:
        left 0.15s linear,
        right 0.15s linear,
        background-color 0.15s linear;
    }
    &.mpm_nav-next {
      transition:
        left 0.15s linear,
        right 0.15s linear,
        background-color 0.15s linear;
      right: -28px;
    }
    .svg-icon {
      opacity: 0.85;
      transition: opacity 0.15s linear;
    }

    &.mpm_nav-prev:hover,
    &.mpm_nav-next:hover {
      background-color: rgba(0, 0, 0, 0.8);
      .svg-icon {
        opacity: 1;
      }
    }
  }

  .tvb-main-category-swiper_slide {
    a {
      text-decoration: none;
      background-color: transparent;
      color: inherit;
      cursor: pointer;
      &:visited {
        color: inherit;
      }
    }

    // :deep(.tvb-main-category-swiper_slide_image) {
    //   --img-hover-transition: transform 0.35s ease-out;
    // }

    .tvb-main-category_swiper-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      :deep(.tvb-player-play) {
        // top: calc(200 / 477 * 100%);
        top: calc(((100% - 76px) / 2) - 48px / 2); // (100% / 2) - icon大小
      }
    }

    .tvb-main-category-swiper_slide_mask {
      width: 100%;
      position: absolute;
      left: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.6);
      padding: 12px 16px 36px;
      line-height: 0;
      z-index: 9;
      --tvb-cmpm2-tit-fs: 22px;
      --tvb-cmpm2-tit-lh: 28px;
      .tvb-swiper_slide_mask-tit {
        @include line-text-ellipsis(1);
        color: #eeeeee;
        font-size: var(--tvb-cmpm2-tit-fs);
        font-weight: 500;
        letter-spacing: 0.5px;
        line-height: var(--tvb-cmpm2-tit-lh);
        // text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.6);
      }
    }
  }

  &.mpm-m,
  &.mpm-s,
  &.mpm-xs {
    padding-bottom: 0px;
    padding-top: 0px;
    padding-left: 12px;

    &.mpm-onlyone {
      padding-top: 0;
      padding-left: 0px;
      // padding-right: 12px;
      .tvb-main-category-swiper {
        padding-bottom: 0;
        .tvb-main-category-swiper_slide {
          aspect-ratio: auto;
          width: 100%;
        }
      }
      .tvb-main-category-mpm_page {
        display: none;
      }
    }

    .tvb-main-category-swiper {
      border-radius: 0;
      box-sizing: border-box;
      padding-bottom: 11px;
      box-shadow: none;

      .tvb-main-category-swiper_slide {
        border-radius: 6px;
        overflow: hidden;
        aspect-ratio: calc(354px / 223px);
        width: auto;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.2);
        // 播放时底黑
        .tvb-main-category_swiper-player.slide-player-playing {
          background-color: #000;
        }
      }
    }

    .tvb-main-category_swiper-player {
      :deep(.tvb-player-container) {
        height: calc(100% - 40px);
      }
      :deep(.tvb-player-play) {
        top: calc(((100% - 40px) / 2) - 40px / 2); // (100% / 2) - icon大小
      }
    }

    .tvb-main-category-swiper_slide_mask {
      padding: 8px 12px;
      --tvb-cmpm2-tit-fs: 16px;
      --tvb-cmpm2-tit-lh: 24px;

      &.mask-playing {
        background-color: rgba($color: #fff, $alpha: 0.08);
      }
    }
  }

  &.mpm-m {
    padding-top: 0px;
    padding-left: 32px;
    // &.mpm-onlyone {
    //   padding-right: 32px;
    // }
    .tvb-main-category-swiper {
      padding-bottom: 13px;
      .tvb-main-category-swiper_slide {
        border-radius: 8px;
        aspect-ratio: calc(936px / 592px);
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
      }
    }
    .tvb-main-category_swiper-player {
      :deep(.tvb-player-container) {
        height: calc(100% - 52px);
      }
      :deep(.tvb-player-play) {
        top: calc(((100% - 52px) / 2) - 48px / 2); // (100% / 2) - icon大小
      }
    }
    .tvb-main-category-swiper_slide_mask {
      padding: 12px 16px;
      --tvb-cmpm2-tit-fs: 22px;
      --tvb-cmpm2-tit-lh: 28px;
    }
  }

  // 分页器
  .tvb-main-category-mpm_page {
    position: absolute;
    left: 16px;
    bottom: 16px;
    z-index: 1;
    user-select: none;

    .tvb-main-category-mpm_page_wrap {
      display: flex;

      .tvb-main-category-mpm_dot {
        height: 8px;
        width: 8px;
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        transition: background-color 0.15s linear;
        cursor: pointer;

        & + .tvb-main-category-mpm_dot {
          margin-left: 8px;
        }

        &.active {
          background-color: rgba(255, 255, 255, 0.8);
        }
        &.hover:hover {
          background-color: rgba(255, 255, 255, 0.8);
        }
      }
    }
  }

  &.mpm-m,
  &.mpm-s,
  &.mpm-xs {
    .tvb-main-category-mpm_page {
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      .tvb-main-category-mpm_dot {
        height: 3px;
        background-color: rgba(163, 178, 188, 0.35);
        border-radius: 2px;
        cursor: auto;
        & + .tvb-main-category-mpm_dot {
          margin-left: 4px;
        }
        &.active {
          background-color: #818d95;
        }
      }
    }
  }

  &.mpm-m {
    .tvb-main-category-mpm_page .tvb-main-category-mpm_dot {
      width: 12px;
      cursor: pointer;
      & + .tvb-main-category-mpm_dot {
        margin-left: 6px;
      }
      &:hover {
        background-color: #818d95;
      }
    }
  }
}
</style>
